import { useState, useMemo } from 'react';
import { EmptyStateCard } from '../EmptyStateCard';
import { HeatmapGrid, HeatmapModeToggle, type HeatmapMode, type HeatmapColumn, type HeatmapRow } from './HeatmapGrid';
import { useGetFacilitiesComparisonQuery } from '../../api/emissions';
import { SCOPE_COLORS } from '../../config/scopeBranding';
import { SCOPE_COLUMNS, ACTIVITY_COLUMNS, buildActivityValuesFromBreakdown } from './heatmapColumns';
import { useAppSelector } from '@reducers/index';
import { getCurrentInitiativeTree } from '@selectors/initiative';
import type { FacilityEmissionData } from '../../types/emissions';

interface FacilitiesHeatmapPanelProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
}

export const FacilitiesHeatmapPanel = ({ initiativeId, periodStart, periodEnd, includeChildren }: FacilitiesHeatmapPanelProps) => {
  const [mode, setMode] = useState<HeatmapMode>('scope');
  const { data, isLoading } = useGetFacilitiesComparisonQuery({ initiativeId, periodStart, periodEnd, includeChildren });
  const allChildren = useAppSelector(getCurrentInitiativeTree);

  // Merge: show ALL child reporting levels, filling zero-emission placeholders for those not in API response
  const facilities = useMemo((): FacilityEmissionData[] => {
    const apiFacilities = data?.facilities ?? [];
    if (allChildren.length === 0) return apiFacilities;
    const children = allChildren;

    const apiMap = new Map(apiFacilities.map((f) => [f._id, f]));
    const merged: FacilityEmissionData[] = children.map((child) => {
      const existing = apiMap.get(child._id);
      if (existing) return existing;
      return { _id: child._id, name: child.name, code: child.code, scopes: [], totalCo2e: 0, totalActivities: 0 };
    });

    merged.sort((a, b) => {
      if (a.totalCo2e > 0 && b.totalCo2e === 0) return -1;
      if (a.totalCo2e === 0 && b.totalCo2e > 0) return 1;
      if (a.totalCo2e > 0 && b.totalCo2e > 0) return b.totalCo2e - a.totalCo2e;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    return merged;
  }, [data?.facilities, allChildren]);

  const columns: HeatmapColumn[] = useMemo(() => {
    if (mode === 'scope') {
      return SCOPE_COLUMNS.map((c) => ({ key: c.key, label: c.label, color: SCOPE_COLORS[c.key] }));
    }
    return ACTIVITY_COLUMNS.map((c) => ({ key: c.key, label: c.label }));
  }, [mode]);

  const rows: HeatmapRow[] = useMemo(() => {
    return facilities.map((facility) => {
      const scopeMap: Record<string, number> = {};
      for (const s of facility.scopes) {
        scopeMap[s.scope] = s.co2e;
      }

      const values = mode === 'scope'
        ? scopeMap
        : buildActivityValuesFromBreakdown(facility.activityTypes ?? []);

      return {
        id: facility._id,
        label: (
          <div>
            <span className='tw:truncate tw:block tw:max-w-[200px]'>{facility.name || 'Unknown'}</span>
            {facility.code && <span className='tw:text-muted-foreground tw:text-[10px]'>{facility.code}</span>}
          </div>
        ),
        values,
        total: facility.totalCo2e,
      };
    });
  }, [facilities, mode]);

  if (!isLoading && facilities.length === 0) {
    return (
      <EmptyStateCard
        title='Reporting Level Heatmap'
        message='No reporting levels with emissions data found for this period.'
      />
    );
  }

  return (
    <HeatmapGrid
      title={mode === 'scope' ? 'Reporting Level × Scope' : 'Reporting Level × Activity Type'}
      description='Color intensity represents emission magnitude. Empty cells indicate no data for that combination.'
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      headerExtra={<HeatmapModeToggle value={mode} onChange={setMode} />}
    />
  );
};
